export type Service = {
  id: number;
  slug: string;
  name: string;
  shortText: string;
  detailText: string[];
  image: string;
  tags: string[];
};

export type TeamMember = {
  id: number;
  slug: string;
  name: string;
  role: string;
  shortBio: string;
  detailBio: string[];
  qualifications: string[];
  portrait: string;
  accent: string;
};

export type Course = {
  weekday: string;
  title: string;
  time: string;
  type: "RehaSport" | "Mitgliederkurs";
  condition: string;
};

export type TourLink = {
  yaw: number;
  target: string;
};

export type TourScene = {
  slug: string;
  title: string;
  roomType: string;
  description: string;
  image: string;
  pano: string;
  panoPreview: string;
  yaw: number;
  pitch?: number;
  links: TourLink[];
};

export const globalSettings = {
  practiceName: "TheraBOX",
  claim: "Physio | Reha | Training",
  address: "Lazarettstraße 3-5, 66280 Sulzbach",
  phone: "06897 / 9669620",
  coursePhone: "06897 / 7290989",
  email: "info@therabox.de",
  website: "therabox.de",
  openingHours: "Montag bis Freitag 08:00-18:00",
  mapUrl: "https://www.google.com/maps/search/?api=1&query=Lazarettstraße%203-5%2066280%20Sulzbach",
  socialLinks: [] as Array<{ label: string; href: string }>,
  copyrightYear: "2026"
};

export const stats = [
  { value: "1500+", label: "betreute Patientinnen und Patienten" },
  { value: "7+", label: "Jahre Praxiserfahrung" },
  { value: "8+", label: "Therapie- und Trainingsprofis" }
];

export const services: Service[] = [
  {
    id: 1,
    slug: "krankengymnastik",
    name: "Krankengymnastik",
    shortText: "Individuelle aktive und passive Behandlung zur Förderung von Beweglichkeit, Kraft und Heilungsprozess.",
    detailText: [
      "Krankengymnastik ist die klassische Basis der Physiotherapie: eine aktive und passive Bewegungstherapie, die sich am individuellen Befund orientiert. Am Anfang steht ein genauer Blick auf Beweglichkeit, Kraft, Schmerzverhalten und Alltagsanforderungen - daraus entsteht ein Behandlungsplan, der zu Ihnen passt.",
      "In der Behandlung kombinieren wir gezielte Übungen mit manuellen Techniken: Mobilisation eingeschränkter Gelenke, Dehnung verkürzter Strukturen, Kräftigung schwacher Muskulatur und Schulung von Koordination und Haltung. Sie arbeiten dabei aktiv mit, denn nachhaltige Verbesserung entsteht durch Bewegung, nicht durch Schonung.",
      "Typische Anlässe sind Rücken- und Nackenbeschwerden, Zustand nach Operationen oder Verletzungen, Gelenkarthrosen, Haltungsprobleme oder länger bestehende Bewegungseinschränkungen. Auch zur Vorbeugung, etwa bei einseitiger beruflicher Belastung, ist Krankengymnastik sinnvoll.",
      "Zu jeder Einheit gehören Übungen für zu Hause: kurz, machbar und auf Ihren Alltag zugeschnitten. So übertragen Sie Therapieerfolge in Beruf, Familie und Sport - und bleiben unabhängig von der Behandlungsbank.",
      "Krankengymnastik wird in der Regel ärztlich verordnet. Bringen Sie Ihre Verordnung einfach zum ersten Termin mit - alles Weitere, von der Terminplanung bis zur Abstimmung mit Ihrer Ärztin oder Ihrem Arzt, übernehmen wir."
    ],
    image: "/images/services/generated/krankengymnastik.webp",
    tags: ["Physiotherapie", "Bewegung", "Alltag"]
  },
  {
    id: 2,
    slug: "manuelle-therapie",
    name: "Manuelle Therapie",
    shortText: "Zertifizierte Behandlung reversibler Funktionsstörungen des Bewegungssystems.",
    detailText: [
      "Die Manuelle Therapie ist eine zertifizierte Weiterbildung innerhalb der Physiotherapie und beschäftigt sich mit reversiblen Funktionsstörungen des Bewegungssystems: Gelenke, Muskeln und Nerven werden gezielt untersucht und behandelt.",
      "Am Anfang steht eine strukturierte Befundung: Welches Gelenk ist eingeschränkt? Ist die Ursache eher muskulär, gelenkig oder neural? Aus dieser Analyse leiten wir präzise Handgriffe ab - etwa Gelenkmobilisationen, Traktionen oder Weichteiltechniken.",
      "Typische Beschwerdebilder sind Blockierungsgefühle an Wirbelsäule und Gelenken, Hexenschuss, Schiefhals, Schulter- und Ellenbogenschmerzen, Lumbalgien oder Bewegungseinschränkungen nach längerer Ruhigstellung.",
      "Manuelle Therapie ist keine reine passive Anwendung: Auf die Mobilisation folgt immer die aktive Stabilisation. Die wiedergewonnene Beweglichkeit wird durch gezielte Übungen gesichert, damit die Beschwerden nicht zurückkehren.",
      "Die Behandlung erfolgt auf ärztliche Verordnung (Heilmittel 'MT'). Unsere Therapeuten verfügen über die entsprechende Zertifikatsausbildung und bilden sich regelmäßig fort."
    ],
    image: "/images/services/generated/manuelle-therapie.webp",
    tags: ["Gelenke", "Schmerz", "Funktion"]
  },
  {
    id: 3,
    slug: "mulligan-concept",
    name: "Mulligan Concept",
    shortText: "Mobilisation with Movement verbindet passive Mobilisation mit aktiver Bewegung.",
    detailText: [
      "Das Mulligan Concept - 'Mobilisation with Movement' - verbindet passive Gelenkmobilisation durch die Therapeutin oder den Therapeuten mit Ihrer aktiven Bewegung. Das Besondere: Die Behandlung findet in der Bewegung statt, die im Alltag Beschwerden bereitet.",
      "Dabei hält der Therapeut das Gelenk in einer korrigierten Position, während Sie die schmerzhafte Bewegung aktiv ausführen. Gelingt die Bewegung dadurch schmerzfrei, wird sie mehrfach wiederholt - das Nervensystem lernt die schmerzfreie Bewegung neu.",
      "Das Konzept eignet sich besonders bei eingeschränkter Kopfdrehung, Tennisellenbogen, Sprunggelenksbeschwerden nach Umknicken, Knie- und Schulterproblemen sowie bei bestimmten Formen von Kopfschmerzen und Schwindel mit Ursprung in der Halswirbelsäule.",
      "Mulligan-Techniken sind sanft und richten sich strikt nach der Schmerzfreiheit: Eine Technik wird nur eingesetzt, wenn sie die Bewegung sofort spürbar verbessert. Ergänzende Eigenübungen und Tape-Anlagen sichern das Ergebnis zwischen den Terminen.",
      "Als spezielle Technik fließt das Mulligan Concept in krankengymnastische und manualtherapeutische Behandlungen ein - sprechen Sie uns an, wenn Sie wissen möchten, ob es für Ihr Beschwerdebild geeignet ist."
    ],
    image: "/images/services/generated/mulligan-concept.webp",
    tags: ["Mobilisation", "Funktion", "Bewegung"]
  },
  {
    id: 4,
    slug: "manuelle-lymphdrainage",
    name: "Manuelle Lymphdrainage",
    shortText: "Sanfte Technik zur Unterstützung des Abtransports von Gewebeflüssigkeit.",
    detailText: [
      "Die Manuelle Lymphdrainage (MLD) ist eine sanfte, großflächige Behandlungstechnik, die den Abtransport von Gewebeflüssigkeit über das Lymphsystem anregt. Mit rhythmischen, kreisenden Griffen wird die Spannung im Gewebe gesenkt und der Lymphfluss unterstützt.",
      "Eingesetzt wird die MLD vor allem bei Lymphödemen und Lipödemen, nach Operationen und Verletzungen mit Schwellung, nach Brustkrebs-Operationen mit Entfernung von Lymphknoten sowie begleitend nach Bestrahlungen.",
      "Die Behandlung ist ausgesprochen sanft - viele Patientinnen und Patienten empfinden sie als entspannend. Je nach Verordnung dauert eine Einheit 30, 45 oder 60 Minuten. Bei ausgeprägten Ödemen gehört zur komplexen Entstauung auch die Versorgung mit Kompression.",
      "Ergänzend beraten wir Sie zu Eigenmaßnahmen: Hautpflege, Bewegungsübungen zur Aktivierung der Muskelpumpe und Verhalten im Alltag, damit das Behandlungsergebnis möglichst lange anhält.",
      "Manuelle Lymphdrainage wird ärztlich verordnet. Unsere Therapeutinnen und Therapeuten besitzen die entsprechende Zusatzqualifikation und stimmen die Behandlung bei Bedarf mit Ihrer Ärztin oder Ihrem Arzt ab."
    ],
    image: "/images/services/generated/manuelle-lymphdrainage.webp",
    tags: ["Ödeme", "Sanft", "Nachsorge"]
  },
  {
    id: 5,
    slug: "sportphysiotherapie",
    name: "Sportphysiotherapie",
    shortText: "Betreuung von ambitionierten Sportlerinnen und Sportlern in Aufbau, Training und Reha.",
    detailText: [
      "Sportphysiotherapie begleitet Sportlerinnen und Sportler durch Belastungsaufbau, Training und Rehabilitation - vom Freizeitläufer bis zum Leistungssport. Unser Team bringt Erfahrung aus der Betreuung von Vereins- und Leistungssport mit, unter anderem aus Fußball, Handball und Triathlon.",
      "Nach Verletzungen oder Operationen geht es zunächst um die Wiederherstellung von Beweglichkeit und Belastbarkeit. Anschließend wird sportartspezifisch aufgebaut: Sprungkraft, Schnelligkeit, Richtungswechsel oder Wurfbelastung werden schrittweise und kontrolliert gesteigert.",
      "Return-to-Sport-Entscheidungen treffen wir nicht nach Gefühl, sondern anhand funktioneller Tests: Kraftvergleiche im Seitenvergleich, Sprung- und Stabilitätstests zeigen objektiv, ob die Rückkehr auf den Platz sicher ist.",
      "Auch ohne Verletzung profitieren Sportler von der Sportphysiotherapie: Screening von Bewegungsmustern, Behandlung von Überlastungsbeschwerden, Regenerationsmaßnahmen und Beratung zur Trainingssteuerung beugen Ausfällen vor.",
      "In Kombination mit unserem Trainingsbereich können Reha und Athletiktraining unter einem Dach stattfinden - mit kurzen Wegen zwischen Behandlungsbank und Gerät."
    ],
    image: "/images/services/generated/sportphysiotherapie.webp",
    tags: ["Sport", "Reha", "Leistung"]
  },
  {
    id: 6,
    slug: "kinesio-taping",
    name: "Kinesio Taping",
    shortText: "Tape-Anlagen zur Begleitung von Schmerzen und Bewegungseinschränkungen.",
    detailText: [
      "Kinesio Taping arbeitet mit elastischen Baumwollbändern, die direkt auf die Haut geklebt werden. Die Tapes bewegen sich mit und üben bei jeder Bewegung einen leichten Zug auf die Haut aus - das beeinflusst Muskelspannung, Durchblutung und Körperwahrnehmung.",
      "Je nach Anlagetechnik kann ein Tape Muskeln aktivieren oder entspannen, Gelenke bei voller Beweglichkeit unterstützen, Schwellungen beim Abfließen helfen oder schlicht daran 'erinnern', eine günstige Haltung einzunehmen.",
      "Typische Einsatzgebiete sind Verspannungen in Nacken und Rücken, Überlastungsbeschwerden an Sehnen, Stabilisierung nach Umknick-Verletzungen, Unterstützung nach Muskelverletzungen im Sport sowie begleitend zur Lymphdrainage.",
      "Kinesio Taping ist eine ergänzende Maßnahme: Es ersetzt keine Therapie, kann deren Wirkung aber zwischen den Behandlungsterminen verlängern. Die Tapes bleiben in der Regel mehrere Tage auf der Haut und sind wasserfest - duschen und Sport sind kein Problem.",
      "Wir stimmen jede Anlage individuell auf Beschwerdebild und Aktivität ab. Sprechen Sie uns an, wenn Sie ein Tape als Ergänzung zu Ihrer Behandlung oder vor einem Wettkampf wünschen."
    ],
    image: "/images/services/generated/kinesio-taping.webp",
    tags: ["Tape", "Bewegung", "Ergänzung"]
  },
  {
    id: 7,
    slug: "kiefergelenk",
    name: "Kiefergelenk / CMD",
    shortText: "Behandlung von Kiefergelenk-Beschwerden im Zusammenhang mit Haltung und Kopfschmerz.",
    detailText: [
      "Beschwerden am Kiefergelenk - Fachbegriff craniomandibuläre Dysfunktion (CMD) - äußern sich vielfältig: Knacken oder Reiben beim Kauen, eingeschränkte Mundöffnung, nächtliches Zähneknirschen, Gesichtsschmerz, aber auch Kopfschmerzen, Nackenverspannungen und Ohrgeräusche können dazugehören.",
      "Kiefer, Halswirbelsäule und Haltung bilden eine funktionelle Einheit. In der Befundung betrachten wir deshalb nicht nur das Kiefergelenk selbst, sondern auch Kaumuskulatur, Kopfgelenke, Haltung und Stressfaktoren wie Zähnepressen am Tag oder in der Nacht.",
      "Die Behandlung kombiniert manuelle Techniken am Kiefergelenk und an der Kaumuskulatur (auch von innen, mit Handschuh), Weichteiltechniken an Nacken und Schultergürtel sowie Übungen zur Selbstmobilisation und Entspannung.",
      "Häufig arbeiten wir eng mit Zahnärztinnen, Zahnärzten und Kieferorthopäden zusammen - etwa wenn eine Aufbissschiene angepasst wurde. Die physiotherapeutische CMD-Behandlung wird in der Regel vom Zahnarzt verordnet.",
      "Viele CMD-Patientinnen und -Patienten haben einen langen Leidensweg hinter sich. Eine strukturierte Befundung schafft oft schon im ersten Termin Klarheit über die Zusammenhänge - und damit einen realistischen Behandlungsplan."
    ],
    image: "/images/services/generated/kiefergelenk.webp",
    tags: ["CMD", "Kiefer", "Haltung"]
  },
  {
    id: 8,
    slug: "elektrotherapie",
    name: "Elektrotherapie",
    shortText: "Unterstützende Anwendungen mit angepassten Stromformen und Ultraschall.",
    detailText: [
      "Elektrotherapie nutzt elektrische Reizströme und Ultraschall, um Heilungsprozesse zu unterstützen. Je nach Stromform wirkt die Anwendung schmerzlindernd, durchblutungsfördernd, muskelentspannend oder muskelaktivierend.",
      "Zum Einsatz kommen unter anderem TENS-Ströme zur Schmerzlinderung, mittelfrequente Ströme zur Muskelkräftigung nach Operationen oder Ruhigstellung sowie Ultraschall zur lokalen Behandlung von Sehnenansätzen und Bändern.",
      "Elektrotherapie ist bei uns kein Ersatz für aktive Therapie, sondern deren Ergänzung: Sie bereitet Gewebe auf die Behandlung vor, unterstützt zwischen den Einheiten oder hilft in schmerzhaften Phasen, in denen aktive Übungen noch nicht möglich sind.",
      "Stromform, Intensität, Dauer und Frequenz werden individuell auf Verordnung und Therapieziel abgestimmt. Die Anwendung ist gut verträglich; über mögliche Gegenanzeigen - etwa Herzschrittmacher oder Metallimplantate im Behandlungsgebiet - klären wir vorab auf.",
      "Elektrotherapie und Ultraschall können einzeln oder ergänzend zu Krankengymnastik und Manueller Therapie verordnet werden."
    ],
    image: "/images/services/generated/elektrotherapie.webp",
    tags: ["Unterstützung", "Ultraschall", "Therapie"]
  },
  {
    id: 9,
    slug: "neurologische-behandlung-nach-pnf",
    name: "Neurologische Behandlung nach PNF",
    shortText: "Therapie bei Schädigungen des zentralen Nervensystems mit Fokus auf Alltag und Mobilität.",
    detailText: [
      "PNF steht für Propriozeptive Neuromuskuläre Fazilitation - ein bewährtes Behandlungskonzept, das über gezielte Reize (Druck, Dehnung, Widerstand) das Zusammenspiel von Nerven und Muskeln verbessert. Behandelt wird in dreidimensionalen Bewegungsmustern, die natürlichen Alltagsbewegungen entsprechen.",
      "Eingesetzt wird PNF vor allem bei neurologischen Erkrankungen: nach Schlaganfall, bei Multipler Sklerose, Morbus Parkinson oder nach Verletzungen des Nervensystems. Aber auch in der Orthopädie hilft das Konzept, gestörte Bewegungsmuster neu zu bahnen.",
      "Im Mittelpunkt stehen alltagsrelevante Ziele: sicher vom Stuhl aufstehen, Treppen steigen, das Gangbild verbessern, die Rumpfkontrolle stärken oder den Arm wieder gezielt einsetzen. Die Therapie holt Sie dort ab, wo Sie aktuell stehen.",
      "PNF lebt von Wiederholung und Steigerung: Bewegungen werden erst geführt, dann gegen Widerstand und schließlich selbstständig ausgeführt. Angehörige beziehen wir auf Wunsch ein, damit Übungen auch zu Hause gelingen.",
      "Die Behandlung erfolgt auf ärztliche Verordnung (KG-ZNS nach PNF durch entsprechend zertifizierte Therapeuten). Bei chronischen neurologischen Erkrankungen begleiten wir Sie gerne langfristig."
    ],
    image: "/images/services/generated/neurologische-behandlung-nach-pnf.webp",
    tags: ["Neurologie", "Mobilität", "Alltag"]
  },
  {
    id: 10,
    slug: "krankengymnastik-am-gerat",
    name: "Krankengymnastik am Gerät",
    shortText: "Aktive Trainingstherapie an medizinischen Trainingsgeräten unter Anleitung.",
    detailText: [
      "Krankengymnastik am Gerät (KGG) ist aktive Trainingstherapie an medizinischen Geräten - unter therapeutischer Anleitung und mit klarem Plan. Sie schließt die Lücke zwischen klassischer Behandlungsbank und selbstständigem Training.",
      "Am Anfang stehen Befund und Zielklärung: Was soll wieder möglich werden - schmerzfrei sitzen, schwere Kisten heben, joggen? Daraus entsteht ein individueller Trainingsplan mit Übungen für Kraft, Ausdauer, Beweglichkeit und Koordination.",
      "Trainiert wird in kleinen Gruppen von maximal drei Personen, betreut durch eine Therapeutin oder einen Therapeuten. Geräteeinstellungen, Gewichte und Wiederholungen werden dokumentiert und von Einheit zu Einheit angepasst - Überlastung und Unterforderung werden so gleichermaßen vermieden.",
      "KGG ist besonders wirksam nach Operationen an Knie, Hüfte und Schulter, bei chronischen Rückenschmerzen, nach Bandscheibenvorfällen sowie beim Wiederaufbau nach längerer Trainingspause oder Krankheit.",
      "Die Verordnung erfolgt ärztlich (Heilmittel 'KGG'). Wer nach Ende der Verordnung weitertrainieren möchte, kann nahtlos in unser medizinisches Fitnesstraining wechseln - am selben Gerätepark, mit demselben Betreuungsteam."
    ],
    image: "/images/services/generated/krankengymnastik-am-gerat.webp",
    tags: ["KGG", "Training", "Kraft"]
  }
];

export const teamMembers: TeamMember[] = [
  {
    id: 7,
    slug: "marc-silbersack",
    name: "Marc Silbersack",
    role: "Inhaber, Physiotherapeut",
    shortBio: "Staatsexamen 2009, Praxis- und Leistungssport-Erfahrung.",
    detailBio: [
      "Marc Silbersack verbindet physiotherapeutische Praxiserfahrung mit Betreuung im Leistungssport.",
      "Zu den Stationen und Referenzen zählen City Reha, Olympiastützpunkt, 1. FC Saarbrücken, SV 07 Elversberg, DTU und DHB."
    ],
    qualifications: ["Lymphdrainage", "Sportphysiotherapie", "KGG", "Mulligan", "Kinesiotaping", "Medical Flossing"],
    portrait: "/images/team/marc-silbersack-portrait.webp",
    accent: "#2f7fae"
  },
  {
    id: 9,
    slug: "rolf-schenn",
    name: "Rolf Schenn",
    role: "Inhaber, Physiotherapeut",
    shortBio: "Staatsexamen 2007, Erfahrung aus Praxis CaraVita und Caritasklinikum-Anbindung.",
    detailBio: [
      "Rolf Schenn bringt langjährige physiotherapeutische Erfahrung aus Praxis und klinischem Umfeld ein.",
      "Seine Schwerpunkte liegen unter anderem in manuellen und neurologischen Therapieverfahren."
    ],
    qualifications: ["Manuelle Therapie", "Myologie", "Triggerpunkttherapie", "Re-Core Training", "PNF", "Lymphdrainage", "Dorn-Breuss"],
    portrait: "/images/team/rolf-schenn-portrait.webp",
    accent: "#1f5287"
  },
  {
    id: 10,
    slug: "gretchen-adomait",
    name: "Gretchen Adomait",
    role: "Physiotherapeutin",
    shortBio: "Physiotherapeutin im TheraBOX-Team.",
    detailBio: ["Gretchen Adomait unterstützt Patientinnen und Patienten in physiotherapeutischen Behandlungen."],
    qualifications: ["Physiotherapie"],
    portrait: "/images/team/gretchen-adomait-portrait.webp",
    accent: "#58a535"
  },
  {
    id: 12,
    slug: "marlon-niesen",
    name: "Marlon Niesen",
    role: "Student Bachelor of Arts Fitnessökonomie",
    shortBio: "Unterstützung im Trainings- und Fitnessbereich.",
    detailBio: ["Marlon Niesen begleitet Trainingsabläufe und bringt sportökonomisches Wissen in den Praxisalltag ein."],
    qualifications: ["Fitnessökonomie", "Training"],
    portrait: "/images/team/marlon-niesen-portrait.webp",
    accent: "#1fa885"
  },
  {
    id: 13,
    slug: "lesson-ruster",
    name: "Lesson Ruster",
    role: "Student Bachelor of Arts Sportökonomie",
    shortBio: "Unterstützung im Kurs- und Trainingsumfeld.",
    detailBio: ["Lesson Ruster unterstützt die Betreuung im Trainings- und Kursbereich."],
    qualifications: ["Sportökonomie", "Training"],
    portrait: "/images/team/lesson-ruster-portrait.webp",
    accent: "#7cb342"
  },
  {
    id: 14,
    slug: "corinna-paulus",
    name: "Corinna Paulus",
    role: "Büro-Management",
    shortBio: "Buchhaltung, Organisation und Praxisverwaltung.",
    detailBio: ["Corinna Paulus betreut Organisation, Buchhaltung und administrative Abläufe der Praxis."],
    qualifications: ["Organisation", "Buchhaltung", "Praxisverwaltung"],
    portrait: "/images/team/corinna-paulus-portrait.webp",
    accent: "#c08a3e"
  }
];

export const courses: Course[] = [
  { weekday: "Montag", title: "Reha-Sport Schulter-Nacken-Kurs / sitzend", time: "10:00-10:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Montag", title: "Reha-Sport Schulter-Nacken-Kurs", time: "11:00-11:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Montag", title: "Reha-Sport Wirbelsäulengymnastik", time: "17:00-17:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Montag", title: "Reha-Sport Faszienkurs", time: "18:00-18:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Dienstag", title: "Mitgliederkurs freie Übungen", time: "10:00-10:45", type: "Mitgliederkurs", condition: "Mitgliedsvertrag" },
  { weekday: "Dienstag", title: "Mitgliederkurs Rückenfit", time: "17:15-18:00", type: "Mitgliederkurs", condition: "Mitgliedsvertrag" },
  { weekday: "Mittwoch", title: "Reha-Sport Wirbelsäulengymnastik / sitzend", time: "10:00-10:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Mittwoch", title: "Reha-Sport Wirbelsäulengymnastik", time: "11:00-11:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Mittwoch", title: "Reha-Sport Knie & Hüfte", time: "15:00-15:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Mittwoch", title: "Mitgliederkurs Kraftzirkel, Powerkurs", time: "16:00-16:45", type: "Mitgliederkurs", condition: "Mitgliedsvertrag" },
  { weekday: "Donnerstag", title: "Mitgliederkurs Faszientraining / Dehnen / Entspannung", time: "10:00-10:45", type: "Mitgliederkurs", condition: "Mitgliedsvertrag" },
  { weekday: "Donnerstag", title: "Reha-Sport Faszienkurs", time: "11:00-11:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Donnerstag", title: "Reha-Sport Schulter-Nacken-Kurs", time: "17:15-18:00", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Freitag", title: "Mitgliederkurs Balance", time: "10:00-10:45", type: "Mitgliederkurs", condition: "Mitgliedsvertrag" },
  { weekday: "Freitag", title: "Reha-Sport Knie & Hüfte", time: "11:00-11:45", type: "RehaSport", condition: "RehaSport-Verordnung" },
  { weekday: "Freitag", title: "Mitgliederkurs freie Übungen", time: "16:00-16:45", type: "Mitgliederkurs", condition: "Mitgliedsvertrag" }
];

export const practiceImages = [
  { title: "Empfang & Anmeldung", roomType: "Willkommen", image: "/images/practice/empfang.jpg", tour: "empfang" },
  { title: "Wartebereich & Flur", roomType: "Praxis", image: "/images/practice/flur.jpg", tour: "flur" },
  { title: "Behandlungsraum", roomType: "Therapie", image: "/images/practice/behandlungsraum-1.jpg", tour: "behandlungsraum" },
  { title: "KGG / Krafttraining", roomType: "Training", image: "/images/practice/training-1.jpg", tour: "kgg" },
  { title: "Trainingsbereich", roomType: "Training", image: "/images/practice/training-2.jpg", tour: "trainingsbereich" },
  { title: "Kursraum", roomType: "Kurse", image: "/images/practice/kursraum-1.webp", tour: "kursraum" },
  { title: "Kursraum RehaSport", roomType: "RehaSport", image: "/images/practice/kursraum-2.jpg", tour: "kursraum" },
  { title: "Mobilität / Faszienraum", roomType: "Mobilität", image: "/images/practice/mobilitaet-1.jpg", tour: "mobilitaet" },
  { title: "Faszienbereich", roomType: "Mobilität", image: "/images/practice/mobilitaet-2.jpg", tour: "faszien" }
];

export const tourScenes: TourScene[] = [
  {
    slug: "empfang",
    title: "Empfang & Anmeldung",
    roomType: "Willkommen",
    description:
      "Hier beginnt Ihr Besuch: Anmeldung, Terminvergabe und ein erster Blick in die Praxis - schauen Sie sich ruhig einmal um.",
    image: "/images/practice/empfang.jpg",
    pano: "/images/panos/empfang.jpg",
    panoPreview: "/images/panos/empfang-preview.jpg",
    yaw: -95.64,
    links: [
      { yaw: -29.14, target: "trainingsbereich" },
      { yaw: 52.11, target: "flur" }
    ]
  },
  {
    slug: "flur",
    title: "Wartebereich & Flur",
    roomType: "Praxis",
    description:
      "Kurze Wege: Vom Wartebereich aus erreichen Sie Behandlungsräume und Kursraum - mit Blick ins Grüne.",
    image: "/images/practice/flur.jpg",
    pano: "/images/panos/flur.jpg",
    panoPreview: "/images/panos/flur-preview.jpg",
    yaw: 100,
    links: [
      { yaw: 155.79, target: "empfang" },
      { yaw: 74.5, target: "behandlungsraum" },
      { yaw: -87.45, target: "kursraum" }
    ]
  },
  {
    slug: "behandlungsraum",
    title: "Behandlungsraum",
    roomType: "Physiotherapie",
    description:
      "Einzeltherapie in ruhiger Atmosphäre: Manuelle Therapie, Lymphdrainage und CMD-Behandlung finden hier statt.",
    image: "/images/practice/behandlungsraum-1.jpg",
    pano: "/images/panos/behandlungsraum.jpg",
    panoPreview: "/images/panos/behandlungsraum-preview.jpg",
    yaw: -45,
    pitch: -22,
    links: [{ yaw: -111.16, target: "flur" }]
  },
  {
    slug: "kursraum",
    title: "Kursraum / RehaSport",
    roomType: "Kurse",
    description:
      "Viel Platz und Tageslicht für RehaSport und Mitgliederkurse: von Rückenfit über Balance bis zum Faszientraining.",
    image: "/images/practice/kursraum-1.webp",
    pano: "/images/panos/kursraum.jpg",
    panoPreview: "/images/panos/kursraum-preview.jpg",
    yaw: -45,
    links: [{ yaw: 0, target: "flur" }]
  },
  {
    slug: "trainingsbereich",
    title: "Trainingsbereich",
    roomType: "Training",
    description:
      "Der offene Trainingsbereich verbindet medizinische Geräte, freie Übungen und Zirkeltraining unter therapeutischer Begleitung.",
    image: "/images/practice/training-2.jpg",
    pano: "/images/panos/trainingsbereich.jpg",
    panoPreview: "/images/panos/trainingsbereich-preview.jpg",
    yaw: 0,
    links: [
      { yaw: 138.03, target: "empfang" },
      { yaw: 0, target: "kgg" },
      { yaw: -139.32, target: "faszien" }
    ]
  },
  {
    slug: "kgg",
    title: "KGG / Krafttraining",
    roomType: "Training",
    description:
      "Krankengymnastik am Gerät und gesundes Krafttraining - angeleitet, dosiert und auf der Trainingskarte dokumentiert.",
    image: "/images/practice/training-1.jpg",
    pano: "/images/panos/kgg.jpg",
    panoPreview: "/images/panos/kgg-preview.jpg",
    yaw: 0,
    links: [
      { yaw: -141.2, target: "trainingsbereich" },
      { yaw: -57.06, target: "mobilitaet" }
    ]
  },
  {
    slug: "faszien",
    title: "Geräte- & Faszienbereich",
    roomType: "Training",
    description:
      "Heller Gerätebereich an der Fensterfront - hier wird Kraft, Beweglichkeit und Faszienarbeit kombiniert.",
    image: "/images/practice/mobilitaet-2.jpg",
    pano: "/images/panos/faszien.jpg",
    panoPreview: "/images/panos/faszien-preview.jpg",
    yaw: 60,
    links: [{ yaw: -37.67, target: "trainingsbereich" }]
  },
  {
    slug: "mobilitaet",
    title: "Mobilität / Faszienraum",
    roomType: "Mobilität",
    description:
      "Bälle, Stepper und Kleingeräte für Mobilisation, Dehnung und Faszienarbeit - die letzte Station des Rundgangs.",
    image: "/images/practice/mobilitaet-1.jpg",
    pano: "/images/panos/mobilitaet.jpg",
    panoPreview: "/images/panos/mobilitaet-preview.jpg",
    yaw: 30,
    links: [{ yaw: 124.3, target: "kgg" }]
  }
];

export const legalPages = {
  privacy: {
    title: "Datenschutzerklärung",
    body: [
      "Stand: Mai 2026",
      "1. Verantwortliche Stelle: Verantwortlich für die Datenverarbeitung auf dieser Website ist die RiVita Physiotherapy GbR, TheraBOX - Physio | Reha | Training, Marc Silbersack und Rolf Schenn, Lazarettstraße 3-5, 66280 Sulzbach. Kontakt: info@therabox.de, Telefon: 06897 / 9669620.",
      "2. Allgemeine Hinweise: Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung dieser Website, zur Bearbeitung von Anfragen oder zur Erfüllung gesetzlicher Pflichten erforderlich ist. Personenbezogene Daten sind alle Informationen, mit denen Sie persönlich identifiziert werden können.",
      "3. Aufruf der Website und Server-Logfiles: Beim Besuch der Website werden technisch notwendige Zugriffsdaten verarbeitet. Dazu können IP-Adresse, Datum und Uhrzeit der Anfrage, aufgerufene Seite, übertragene Datenmenge, Browsertyp, Betriebssystem, Referrer-URL und Statuscodes gehören. Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO, um die Website sicher und stabil bereitzustellen und Missbrauch erkennen zu können.",
      "4. Kontaktformular und E-Mail-Kontakt: Wenn Sie uns über das Kontaktformular oder per E-Mail kontaktieren, verarbeiten wir die von Ihnen eingegebenen Angaben, insbesondere Name, E-Mail-Adresse, Telefonnummer und Nachricht. Die Verarbeitung erfolgt zur Bearbeitung Ihrer Anfrage auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, soweit die Anfrage auf einen Vertrag oder vorvertragliche Maßnahmen gerichtet ist, ansonsten auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.",
      "5. Pflichtangaben im Kontaktformular: Pflichtfelder sind erforderlich, damit wir Ihre Anfrage zuordnen und beantworten können. Ohne diese Angaben kann die Anfrage nicht über das Formular versendet werden.",
      "6. Speicherdauer: Wir speichern personenbezogene Daten nur so lange, wie es für den jeweiligen Zweck erforderlich ist. Anfragen werden gelöscht, sobald sie abschließend bearbeitet sind und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.",
      "7. Hosting und Auftragsverarbeitung: Die Website wird technisch auf Servern eines Hosting- bzw. Infrastruktur-Dienstleisters betrieben. Dabei können Zugriffsdaten und Formularanfragen verarbeitet werden. Soweit erforderlich, wird mit Dienstleistern ein Vertrag zur Auftragsverarbeitung nach Art. 28 DSGVO geschlossen.",
      "8. Cookies und externe Dienste: Diese Website setzt nach aktuellem technischen Stand keine Analyse- oder Marketing-Cookies ein. Externe Karten-, Statistik- oder Trackingdienste werden nicht automatisch geladen. Externe Links, etwa zu Routenplanern oder Karten, öffnen erst nach aktivem Anklicken durch die Nutzerin oder den Nutzer.",
      "9. Empfänger personenbezogener Daten: Personenbezogene Daten werden nur intern oder an technisch notwendige Dienstleister weitergegeben, soweit dies zur Bearbeitung Ihrer Anfrage oder zum Betrieb der Website erforderlich ist. Eine Weitergabe zu Werbezwecken findet nicht statt.",
      "10. Ihre Rechte: Sie haben nach Maßgabe der gesetzlichen Voraussetzungen das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch gegen bestimmte Verarbeitungen. Außerdem können Sie eine erteilte Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen.",
      "11. Beschwerderecht: Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren. Zuständig ist unter anderem die Landesbeauftragte oder der Landesbeauftragte für Datenschutz des Saarlandes.",
      "12. Datensicherheit: Die Website wird über HTTPS ausgeliefert, sobald das Zertifikat auf dem Zielhost eingerichtet ist. Bitte beachten Sie, dass Kommunikation per E-Mail trotz Transportverschlüsselung Sicherheitslücken aufweisen kann.",
      "Hinweis vor Go-live: Hostinganbieter, Auftragsverarbeitung, konkrete Log-Speicherdauer und zuständige Datenschutzaufsicht sollten vor Veröffentlichung final geprüft und bei Bedarf ergänzt werden."
    ]
  },
  disclaimer: {
    title: "Haftungsausschluss",
    body: [
      "Inhalte dieser Website: Die Inhalte dieser Website wurden mit Sorgfalt erstellt und dienen der allgemeinen Information über TheraBOX, Physiotherapie, RehaSport, medizinisches Training und damit verbundene Leistungen. Für Richtigkeit, Vollständigkeit und Aktualität der Inhalte übernehmen wir jedoch keine Gewähr.",
      "Keine medizinische Beratung: Medizinische und therapeutische Informationen auf dieser Website ersetzen keine persönliche Untersuchung, Diagnose, Beratung oder Behandlung durch Ärztinnen, Ärzte, Therapeutinnen oder Therapeuten. Bei akuten Beschwerden, Notfällen oder Unsicherheiten wenden Sie sich bitte unmittelbar an eine geeignete medizinische Stelle.",
      "Behandlungsentscheidungen: Die Darstellung einzelner Leistungen bedeutet nicht, dass diese im Einzelfall geeignet oder verordnungsfähig sind. Ob und welche Behandlung sinnvoll ist, richtet sich nach Befund, ärztlicher Verordnung, individuellen Voraussetzungen und therapeutischer Einschätzung.",
      "Externe Links: Diese Website kann Links zu externen Websites enthalten. Auf deren Inhalte haben wir keinen Einfluss. Für fremde Inhalte ist stets der jeweilige Anbieter oder Betreiber verantwortlich. Bei Bekanntwerden rechtswidriger Inhalte entfernen wir entsprechende Links unverzüglich.",
      "Urheberrecht: Inhalte, Texte, Bilder, Grafiken und Gestaltung dieser Website unterliegen dem Urheberrecht. Eine Nutzung außerhalb der gesetzlichen Grenzen ist nur mit vorheriger Zustimmung der Rechteinhaber zulässig.",
      "Verfügbarkeit: Wir bemühen uns um einen störungsarmen Betrieb der Website, können jedoch keine jederzeitige Verfügbarkeit garantieren. Technische Änderungen, Wartung oder Ausfälle können die Erreichbarkeit zeitweise einschränken.",
      "Hinweis vor Go-live: Dieser Haftungsausschluss sollte vor Veröffentlichung rechtlich geprüft und an finale Bildrechte, externe Links und redaktionelle Verantwortlichkeiten angepasst werden."
    ]
  },
  impressum: {
    title: "Impressum",
    body: [
      "Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG)",
      "RiVita Physiotherapy GbR, handelnd unter TheraBOX - Physio | Reha | Training",
      "Vertreten durch die Gesellschafter Marc Silbersack und Rolf Schenn",
      `Anschrift: ${globalSettings.address}`,
      `Telefon: ${globalSettings.phone}`,
      `E-Mail: ${globalSettings.email}`,
      "Website: https://therabox.schnick-schnack.info",
      "Berufsbezeichnung: Physiotherapeut, verliehen in der Bundesrepublik Deutschland.",
      "Zuständige Aufsichtsbehörde: Vor Veröffentlichung bitte zuständige Behörde final prüfen und ergänzen.",
      "Berufsrechtliche Regelungen: Vor Veröffentlichung bitte zuständige Berufsgesetze, Heilmittelrichtlinien und weitere berufsrechtliche Angaben final prüfen und ergänzen, soweit erforderlich.",
      "Umsatzsteuer-ID oder Wirtschafts-Identifikationsnummer: Vor Veröffentlichung bitte prüfen und ergänzen, sofern eine Angabepflicht besteht.",
      "Inhaltlich verantwortlich gemäß § 18 Abs. 2 MStV: Marc Silbersack und Rolf Schenn, Anschrift wie oben.",
      "Verbraucherstreitbeilegung: Wir sind nicht verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen, sofern keine abweichende gesetzliche Pflicht besteht.",
      "Bild- und Inhaltsrechte: Praxis- und Teamfotos sowie generierte Leistungsbilder werden ausschließlich für diesen Relaunch verwendet. Rechte, Freigaben und Persönlichkeitsrechte sind vor Go-live final zu prüfen.",
      "Hinweis vor Go-live: Dieses Impressum ist fachlich vorbereitet, ersetzt aber keine abschließende rechtliche Prüfung. Insbesondere Aufsichtsbehörde, berufsrechtliche Pflichtangaben und steuerliche Angaben müssen final vom Betreiber bestätigt werden."
    ]
  }
};
